import { Configuration, FrontendApi } from '@ory/client-fetch';
import { type OryClientConfiguration } from '@ory/elements-react';
import { useMemo } from 'react';

import { getExtensionSessionToken } from '@apps/web/lib/extension_session_token';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { rewriteUrlOrigin } from '@apps/web/lib/url_rewrite';

function trimTrailingSlash(pathname: string): string {
    return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function isPlainObject(
    value: object,
): value is Record<string | number | symbol, unknown> {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

function resolveKratosUrl(kratosPublicUrl: string): URL {
    try {
        return new URL(kratosPublicUrl);
    } catch {
        return new URL(kratosPublicUrl, window.location.origin);
    }
}

function resolveKratosBaseUrl(kratosPublicUrl: string): string {
    const resolvedKratosUrl = resolveKratosUrl(kratosPublicUrl);
    return `${resolvedKratosUrl.origin}${trimTrailingSlash(
        resolvedKratosUrl.pathname,
    )}`;
}

function normalizeOryUrl(url: string, kratosPublicUrl: string): string {
    const currentKratosBaseUrl = resolveKratosBaseUrl(kratosPublicUrl);
    const currentWebBaseUrl = window.location.origin;

    for (const [sourceOrigin, replacement] of [
        ['http://localhost:4433', currentKratosBaseUrl],
        ['http://127.0.0.1:4433', currentKratosBaseUrl],
        ['http://localhost:5173', currentWebBaseUrl],
        ['http://127.0.0.1:5173', currentWebBaseUrl],
    ] as const) {
        const normalizedUrl = rewriteUrlOrigin(url, sourceOrigin, replacement);
        if (normalizedUrl) {
            return normalizedUrl;
        }
    }

    return url;
}

export function normalizeOryPayload<T>(value: T, kratosPublicUrl: string): T {
    if (typeof value === 'string') {
        return normalizeOryUrl(value, kratosPublicUrl) as T;
    }

    if (Array.isArray(value)) {
        return value.map((entry) =>
            normalizeOryPayload(entry, kratosPublicUrl),
        ) as T;
    }

    if (value && typeof value === 'object') {
        if (!isPlainObject(value)) {
            return value;
        }

        return Object.fromEntries(
            Object.entries(value).map(([key, entry]) => [
                key,
                normalizeOryPayload(entry, kratosPublicUrl),
            ]),
        ) as T;
    }

    return value;
}

export function useOry() {
    const { kratosPublicUrl } = getRuntimeConfig();
    const extensionSessionToken = getExtensionSessionToken();
    const resolvedKratosPublicUrl = useMemo(
        () => resolveKratosBaseUrl(kratosPublicUrl),
        [kratosPublicUrl],
    );
    const normalizeOryPayloadForResolvedKratosUrl = useMemo(
        () =>
            <T>(value: T): T =>
                normalizeOryPayload(value, resolvedKratosPublicUrl),
        [resolvedKratosPublicUrl],
    );

    const oryClient: FrontendApi = useMemo(() => {
        return new FrontendApi(
            new Configuration({
                basePath: resolvedKratosPublicUrl,
                credentials: extensionSessionToken ? 'omit' : 'include',
                headers: { Accept: 'application/json' },
            }),
        );
    }, [extensionSessionToken, resolvedKratosPublicUrl]);

    const oryElementsConfiguration: OryClientConfiguration = useMemo(
        () => ({
            project: {
                name: 'Phoebe',
                default_locale: 'en',
                locale_behavior: 'force_default',
                // NOTE(isaac): While the below are all logically optional, and don't
                // seem to have any effect at overriding the server-defined values,
                // the types require them to be set.
                default_redirect_url: '/',
                error_ui_url: '',
                login_ui_url: '',
                registration_ui_url: '',
                recovery_ui_url: '',
                settings_ui_url: '',
                verification_ui_url: '',
                recovery_enabled: true,
                registration_enabled: true,
                verification_enabled: true,
            },
            intl: {
                locale: 'en',
                customTranslations: {
                    // https://github.com/ory/elements/blob/main/packages/elements-react/src/locales/en.json
                    en: {
                        'registration.title': 'Sign Up',
                        'identities.messages.1060001':
                            'Reset success - please select a new password!',
                    },
                },
            },
            sdk: { url: resolvedKratosPublicUrl },
        }),
        [resolvedKratosPublicUrl],
    );

    return {
        oryClient,
        oryElementsConfiguration,
        normalizeOryPayload: normalizeOryPayloadForResolvedKratosUrl,
    };
}
